import { useState } from "react"
import { PlayersRepository } from "../../../api/repositories/backend/players"
import { localize } from "../../../common/services/localization"
import { OrangeButton } from "../functional/buttons"
import { SocialInput } from "../functional/inputs"
import { Popup } from "./popup"
import { Socials } from "../../../api/repositories/shared/models/players"

export const EditMediaPopup: React.FC<{
    initialSocials: Socials;
    onSave: (socials: Socials) => void;
    onClose: () => void;
}> = ({ initialSocials, onSave, onClose }) => {
    const [socials, setSocials] = useState<Socials>(initialSocials);

    const [errors, setErrors] = useState<Record<SocialType, string | undefined>>({
        discord: undefined,
        youtube: undefined,
        twitch: undefined,
        discordUserName: undefined
    });

    const repository = new PlayersRepository();

    const validateUrl = (type: SocialType, value: string): boolean => {
        if (!value) return true;

        const patterns = {
            discord: /^https:\/\/(discord\.com|discord\.gg)\/.*/,
            youtube: /^https:\/\/(www\.)?youtube\.com\/.*/,
            twitch: /^https:\/\/(www\.)?twitch\.tv\/.*/,
            discordUserName: /^(?=.{2,32}$)(?!.*\.\.)(?!.*\.$)(?!^\.)[a-z0-9._]+$/i
        };

        return patterns[type].test(value);
    };

    const handleSocialChange = (type: SocialType, value: string) => {
        const trimmedValue = value?.trim();
        const newValue = trimmedValue === '' ? null : trimmedValue;

        if (newValue && !validateUrl(type, newValue)) {
            setErrors(prev => ({
                ...prev,
                [type]: getErrorMessage(type)
            }));
            return;
        } else {
            setErrors(prev => ({ ...prev, [type]: undefined }));
        }

        setSocials(prev => ({
            ...prev,
            [type]: newValue
        }));
    };

    const getErrorMessage = (type: SocialType): string => {
        const messages = {
            discord: "Допустимы только ссылки на discord.com или discord.gg",
            youtube: "Допустимы только ссылки на www.youtube.com",
            twitch: "Допустимы только ссылки на www.twitch.tv",
            discordUserName: "Проверьте правильность discord username"
        };
        return messages[type];
    };

    const handleSave = () => {
        const hasErrors = Object.values(errors).some(error => error !== undefined);
        if (!hasErrors) {
            repository.updateSocials(socials)
                .then(t => t.hasErrors
                    ? alert(t.errors[0])
                    : onSave(socials)
                );
        }
    };

    return <Popup
        onClose={onClose}
        className="px-[0]"
    >
        <div className="flex flex-col gap-[26px]">
            <span className="uppercase font-semibold text-[24px] lg:text-[36px] text-center">
                {localize('edit_media_popup_title')}
            </span>
            <SocialRowBlock
                defaultValue={socials.twitch}
                type="twitch"
                onChange={(value) => handleSocialChange("twitch", value)}
                error={errors.twitch}
            />
            <SocialRowBlock
                defaultValue={socials.youtube}
                type="youtube"
                onChange={(value) => handleSocialChange("youtube", value)}
                error={errors.youtube}
            />
            <SocialRowBlock
                defaultValue={socials.discord}
                type="discord"
                onChange={(value) => handleSocialChange("discord", value)}
                error={errors.discord}
            />
            <SocialRowBlock
                defaultValue={socials.discordUserName}
                type="discordUserName"
                onChange={(value) => handleSocialChange("discordUserName", value)}
                error={errors.discord}
            />
            <div className="w-screen px-[10px] lg:w-full">
                <OrangeButton
                    localText='social_save_btn'
                    onClick={handleSave}
                    className="w-full py-[10px] text-[18px] uppercase"
                    disabled={Object.values(errors).some(error => error !== undefined)}
                />
            </div>
        </div>
    </Popup>
}

const SocialRowBlock: React.FC<{
    type: SocialType;
    onChange: (value: string) => void;
    error?: string;
    defaultValue?: string
}> = ({ type, onChange, error, defaultValue }) => {
    return <div className="flex flex-col gap-[8px] w-screen px-[10px] lg:w-[680px]">
        <SocialInput
            defaultValue={defaultValue}
            className="w-full"
            type={type}
            onChange={onChange}
        />
        {error && <span className="text-red-500 text-sm">{error}</span>}
    </div>
}

type SocialType = keyof Socials;